import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';
import { HosAddendumService } from 'src/app/services/hos-addendum/hos-addendum.service';
import { EsocService } from 'src/app/services/e-soc/esoc.service';
import { MessageHandlerService } from 'src/app/services/message-handler.service';
import { UserDataManager } from "src/app/user/UserDataManager";
import { UtilService } from 'src/app/services/util/util.service';
import { DOB_DATE_FORMAT, RoleID } from 'src/app/common/Constants';
import { endOfToday } from 'src/app/common/calendar-max-date.util';
import { parseDateOnly } from 'src/app/common/date-only.util';
import { countries } from 'src/app/models/country-data';

/**
 * What the server substitutes into the recipient when no name was supplied. It looks like a name in
 * the field and is not one, so the screen treats it as missing wherever a real name is required.
 * Kept in step with HOS_ADDENDUM_UNNAMED_RECIPIENT in the patient service.
 */
const UNNAMED_RECIPIENT = 'Beneficiary / Representative';

/** The recipient details the preparer can type, pick or tick (IOHB-2107). */
type RecipientField = 'name' | 'email' | 'phone' | 'dob' | 'sendViaEmail' | 'sendViaPhone';

@Component({
  selector: 'app-hos-addendum',
  templateUrl: './hos-addendum.component.html',
  styleUrls: ['./hos-addendum.component.scss'],
})
export class HosAddendumComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() selectedEsocRecord: any = null;
  @Input() currentAgencyRole: any = null;
  @Input() currentAgency: any = null;
  @Output() onUpdated = new EventEmitter<any>();

  activeTab: 'upload' | 'history' = 'upload';
  loading: boolean = false;
  uploading: boolean = false;
  /**
   * How much of the file has gone up, 0-100, for the bar the upload shows as E-Sign's does. At 100 the file is up
   * and the server is still preparing it (finding the signature lines, reading the MRN), which it does not report.
   */
  uploadProgress: number = 0;
  /**
   * How the recipient is reached, as ioDoc's "Send via Email" / "Send via Phone" boxes. The server gets
   * e-sign's values - see notificationMethod - and sends and reminds only by what is ticked.
   */
  sendViaEmail: boolean = true;
  sendViaPhone: boolean = false;
  sending: boolean = false;
  downloadingFile: boolean = false;

  // Upload & Form state
  selectedFile: File | null = null;
  reusePatientInfo: boolean = true;
  autoSend: boolean = true;

  recipientName: string = '';
  recipientEmail: string = '';
  additionalEmail: string = '';
  /**
   * Hidden for now (1 Oct): one email and one phone, each with its own "Send via" box. While hidden it
   * is neither checked nor sent, so nobody keeps receiving the addendum at an address nobody can see.
   */
  readonly showAdditionalEmail = false;
  /** The company's Hide "Copy Signing Link" setting, read when the dialog opens. */
  companyHidesCopySigningLink = false;
  recipientPhone: string = '';
  countries = countries;
  /** The dial code shown beside the phone box. Sending without one is why addendum texts failed
   *  with "Phone number must have country code" - the number went out as national digits only. */
  recipientCountryCode: string = countries[0].dial_code;
  /**
   * The dial code this patient's record holds, kept apart from the one in the dropdown.
   *
   * Numbers stored on an addendum carry no code: the backend saves
   * `eSocEmailPhonePreference.phone.number`, which is national digits, and drops the `countryCode`
   * beside it. Reading one of those back must not mean "assume +1", or opening a draft would text a
   * +44 patient a US number - so it falls back to this instead of the default.
   */
  private preferredCountryCode: string = '';
  recipientDob: any = null;
  customMessage: string = '';

  dobDateFormat = DOB_DATE_FORMAT;
  maxDate: Date = endOfToday();

  // History & Active records
  activeDoc: any = null;
  historyDocs: any[] = [];
  allDocs: any[] = [];
  /** Which patient the documents above belong to, so they are never shown against another one. */
  private docsLoadedForEsocId: string | null = null;

  /** The agency's clock lengths, needed to tell whether a running window is near its end. */
  clockConfig: { initialClockValue: number; subsequentClockValue: number; clockUnit: string } = {
    initialClockValue: 5,
    subsequentClockValue: 3,
    clockUnit: 'days',
  };
  /** The start of care on the patient's record, shown as-is when there is one. */
  patientSocDate: Date | null = null;
  /** A date entered here instead. Used by the addendum clock alone, never written to the record. */
  addendumSocDate: Date | null = null;
  socDateInput: Date | null = null;
  /**
   * Which date this addendum's clock runs from. 'record' hands it back to the patient's own start of
   * care; 'custom' anchors it to a date entered here. The choice exists because a start of care long
   * past would otherwise decide the window on its own, and the date CMS measures from is not always
   * the start-of-care date.
   */
  socDateSource: 'record' | 'custom' = 'record';
  savingSocDate: boolean = false;
  socDateMaxDate: Date = new Date();

  /**
   * The date this upload's signing clock runs from, asked at upload.
   *
   * Asked here rather than on the panel because it belongs to the document, not the patient: a start
   * of care does not move, but each subsequent addendum is about a change in care that happened on
   * its own date, so two addendums on one patient legitimately carry two different dates.
   *
   * The kind of date decides the window - a start of care or election date takes the agency's initial
   * clock, a change date its subsequent one - which is why it is asked rather than inferred.
   */
  showUploadDateDialog: boolean = false;
  uploadDateType: 'soc' | 'change' = 'soc';
  uploadDate: Date | null = null;

  // Audit dialog
  auditModalVisible: boolean = false;
  auditEvents: any[] = [];
  selectedAuditDoc: any = null;

  /**
   * Retraction: pulling back an addendum that is already out for signature.
   *
   * Admin only. A reason may be given and is not required; when there is one it goes into the audit
   * trail, which is the only record of why a document that reached a patient was withdrawn.
   */
  /**
   * The document was read at upload to check whose MRN is on it. If it is not this patient's, or the
   * page could not be read at all, the preparer is asked before it goes out. Saying yes is remembered
   * for that one document, so the same document does not ask twice.
   *
   * Remembered by the document's id, not as a yes for the screen: this component is reused for
   * whichever patient is opened next, so a yes kept for the screen carried over to another patient's
   * document and sent it with no warning - the wrong-patient send the warning exists to stop.
   */
  mrnWarningVisible: boolean = false;
  /** The document whose "signature line not found" warning was answered, so it is asked once per document. */
  private placementAcknowledgedDocId: string | null = null;
  /** The upload box's file input, opened again when the preparer chooses to upload a different file. */
  @ViewChild('fileInput') private fileInputRef?: ElementRef<HTMLInputElement>;
  private mrnAcknowledgedDocId: string | null = null;
  /** The document the open warning is about, and the send waiting on the answer. */
  private mrnWarningDocId: string | null = null;
  private sendAwaitingMrnAnswer: (() => void) | null = null;

  retractDialogVisible: boolean = false;
  retractReason: string = "";
  retracting: boolean = false;
  currentUser: any = null;
  private userSubscription: Subscription | null = null;

  constructor(
    private readonly hosAddendumService: HosAddendumService,
    private readonly esocService: EsocService,
    private readonly messageHandlerService: MessageHandlerService,
    private readonly utilService: UtilService,
    private readonly confirmationService: ConfirmationService,
    private readonly userDataManager: UserDataManager,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // user$ outlives this component, so the subscription is released on destroy; otherwise every
    // eSoc screen that shows this keeps an old copy of the component alive.
    this.userSubscription = this.userDataManager.user$.subscribe((user) => {
      this.currentUser = user;
    });
    if (this.selectedEsocRecord) {
      this.initForm();
      this.syncDocsFromRecord();
      this.loadHistory(true);
      this.loadCompanySettings(true);
    }
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.userSubscription = null;
  }

  /**
   * Who may retract. The server enforces this too - this only decides whether the button is shown,
   * and a screen that offers an action the server will refuse is worse than not offering it.
   */
  get isAdmin(): boolean {
    if (!this.currentUser) {
      return false;
    }
    if (this.currentUser.is_super_admin || this.currentUser.is_IOH_admin) {
      return true;
    }
    // An admin in one agency may be only a clinician in this one, and the server judges by this one.
    const agencyId = String(this.resolveAgencyId() || this.selectedEsocRecord?.agencyId || '');
    return (this.currentUser.agency_roles || []).some((entry: any) =>
      String(entry?.agency?.agencyID || entry?.agency?._id || '') === agencyId &&
      (entry?.roles || []).some(
        (role: string) =>
          role === RoleID.AgencyAdmin ||
          role === RoleID.IOHAgencyAdmin ||
          role === RoleID.companyAdmin,
      ),
    );
  }

  /**
   * Only an addendum actually out for signature can be pulled back. One never sent has nothing to
   * retract, and a signed or expired one is finished - withdrawing either would be rewriting history
   * rather than stopping something in flight.
   */
  get canRetract(): boolean {
    const status = this.activeDoc?.status;
    return (
      this.isAdmin && (status === "sent" || status === "pending") && !this.isSigned
    );
  }

  openRetractDialog(): void {
    this.retractReason = "";
    this.retractDialogVisible = true;
  }

  confirmRetract(): void {
    const reason = this.retractReason.trim();
    if (!this.activeDoc?._id) {
      return;
    }
    this.retracting = true;
    this.hosAddendumService
      .retractHosAddendum(
        { hosAddendumId: this.activeDoc._id, reason },
        this.selectedEsocRecord?.agencyId,
      )
      .pipe(finalize(() => (this.retracting = false)))
      .subscribe({
        next: () => {
          this.messageHandlerService.generateToastMessage(
            "success",
            "Success",
            "The addendum has been retracted. It is back to Not Sent and its signing link no longer works.",
          );
          this.retractDialogVisible = false;
          this.retractReason = "";
          this.loadHistory(true);
        },
        error: (err: any) => {
          this.messageHandlerService.generateToastMessage(
            "error",
            "Error",
            err?.error?.message || "Failed to retract the addendum",
          );
        },
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedEsocRecord'] && this.selectedEsocRecord) {
      this.initForm();
      this.syncDocsFromRecord();
      this.loadHistory(true);
      this.loadCompanySettings(true);
    }
    if (changes['visible'] && this.visible) {
      this.syncDocsFromRecord();
      this.loadHistory(true);
      this.loadCompanySettings(false);
    }
  }

  /**
   * The company's HOS Addendum settings, from the same check the ioDoc page runs, asked about the
   * patient's own agency. Hide "Copy Signing Link" is saved on the company, but nothing brought it to
   * this screen, so ticking it changed nothing. `reset` when a different patient opens, so one
   * company's setting is never shown for another.
   */
  private loadCompanySettings(reset: boolean): void {
    if (reset) {
      this.companyHidesCopySigningLink = false;
    }
    const record = this.selectedEsocRecord;
    const agencyId =
      record?.agencyId ||
      record?.patientEsocData?.agencyId ||
      record?.patientEsocData?.patient?.agencyId ||
      this.resolveAgencyId();
    if (!agencyId || !this.hosAddendumService?.checkHosAddendumEnabled) {
      return;
    }
    const esocId = record?.patientEsocData?._id;
    this.hosAddendumService.checkHosAddendumEnabled(String(agencyId)).subscribe({
      next: (res: any) => {
        if (esocId && !this.isStillOnScreen(esocId)) {
          return;
        }
        this.companyHidesCopySigningLink = res?.content?.hideCopySigningLink === true;
      },
      // A display setting that cannot be read leaves the screen as it was.
      error: () => undefined,
    });
  }

  syncDocsFromRecord(): void {
    // This component is reused for whichever patient is opened next, so documents held from the
    // previous one have to go: the send payload carries activeDoc._id, which would otherwise name
    // that patient's addendum under this patient's eSoc. Documents are kept only while the patient
    // is the same, which is what lets a freshly uploaded one survive a listing row that has not
    // caught up yet.
    const esocId = this.selectedEsocRecord?.patientEsocData?._id ?? null;
    if (esocId !== this.docsLoadedForEsocId) {
      this.activeDoc = null;
      this.allDocs = [];
      this.historyDocs = [];
      // The start of care belongs to the patient just as much as the documents do. Left behind, the
      // field would still hold the previous patient's date and Save would write it to this one.
      this.patientSocDate = null;
      this.addendumSocDate = null;
      this.socDateInput = null;
      this.socDateSource = 'record';
      // Set from the patient being loaded rather than cleared. This runs after initForm() and before
      // the populateFromActiveDoc() below, so clearing here emptied the fallback in between and a
      // stored number with no code of its own fell back to the default - which is the whole fault it
      // exists to prevent.
      this.preferredCountryCode = this.knownDialCode(
        this.selectedEsocRecord?.patientEsocData?.eSocEmailPhonePreference?.phone?.countryCode,
      );
      this.clockConfig = { initialClockValue: 5, subsequentClockValue: 3, clockUnit: 'days' };
      this.docsLoadedForEsocId = esocId;
    }

    const latestHos =
      this.selectedEsocRecord?.latestHosAddendum ||
      this.selectedEsocRecord?.patientEsocData?.latestHosAddendum;
    if (latestHos) {
      this.activeDoc = latestHos;
      this.allDocs = [latestHos];
      this.historyDocs = [];
    }

    const hosFiles = this.selectedEsocRecord?.patientEsocData?.hosAddendumFiles;
    if (hosFiles && Array.isArray(hosFiles) && hosFiles.length > 0) {
      this.allDocs = [...hosFiles];
      this.activeDoc = hosFiles.find((d: any) => d.docState !== 'history') || hosFiles[0];
      this.historyDocs = hosFiles.filter((d: any) => d._id?.toString() !== this.activeDoc?._id?.toString());
    }

    // Who it was sent to, whatever its state. This only happened once it was signed, so a sent or draft
    // addendum showed the patient instead - reading as "not stored" - and a Resend then overwrote the
    // representative it had gone to with the patient's details.
    if (this.activeDoc?.recipient) {
      this.populateFromActiveDoc();
    }
  }

  /** A request made for one patient can finish after the dialog has moved on to another. */
  private isStillOnScreen(esocId: string): boolean {
    return this.selectedEsocRecord?.patientEsocData?._id === esocId;
  }

  /** The agency header every HOS Addendum call carries. */
  private resolveAgencyId(): any {
    if (this.currentAgency && typeof this.currentAgency === 'object') {
      return (this.currentAgency as any)._id || (this.currentAgency as any).id;
    }
    return this.currentAgency || this.selectedEsocRecord?.patientEsocData?.patient?.agencyId;
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  initForm(): void {
    this.selectedFile = null;
    this.reusePatientInfo = true;
    this.autoSend = true;
    this.customMessage = '';
    this.populateFromPatient();
  }

  /**
   * The recipient details the preparer typed, picked or ticked by hand (IOHB-2107). Set by the fields' own input
   * events, which fire only for the preparer - never when the screen fills a field, and not when one merely loses
   * focus - and forgotten when the screen changes that field, so a value the screen only showed, such as the
   * recipient read back from the last addendum, never counts. The number and its dial code are one entry: picking a
   * code keeps the number shown with it. Not used while Reuse patient info is unticked: Yes then fills in the patient.
   */
  private enteredByHand = new Set<RecipientField>();

  /** Called by the recipient fields as the preparer types, picks or ticks (IOHB-2107). */
  noteEnteredByHand(field: RecipientField): void {
    this.enteredByHand.add(field);
  }

  /** The recipient details as text, to tell which ones a fill changes (IOHB-2107). */
  private recipientSnapshot(): Record<RecipientField, string> {
    const dob = this.recipientDob instanceof Date ? this.recipientDob.getTime() : this.recipientDob;
    return {
      name: this.recipientName || '',
      email: this.recipientEmail || '',
      // Digits only: the box holds the number masked, the screen writes it bare.
      phone: `${this.recipientCountryCode || ''} ${(this.recipientPhone || '').replace(/\D/g, '')}`,
      dob: String(dob ?? ''),
      sendViaEmail: String(this.sendViaEmail),
      sendViaPhone: String(this.sendViaPhone),
    };
  }

  /**
   * The screen has filled the form: a field it changed no longer holds what the preparer entered, and one it left
   * as it was still does (IOHB-2107).
   */
  private forgetChangedSince(before: Record<RecipientField, string>): void {
    const now = this.recipientSnapshot();
    this.enteredByHand.forEach((field) => {
      if (now[field] !== before[field]) {
        this.enteredByHand.delete(field);
      }
    });
  }

  /**
   * What a refill keeps (IOHB-2107): each field entered by hand and not left blank - spaces alone are blank - and
   * each Send via box ticked or unticked by hand. Not a date of birth typed over the one on the patient's record:
   * that is the date the recipient is checked against, and the field locks to it.
   */
  private enteredAndNotBlank(): Set<RecipientField> {
    const values: Record<RecipientField, unknown> = {
      name: this.recipientName,
      email: this.recipientEmail,
      phone: this.recipientPhone,
      dob: this.patientDob ? null : this.recipientDob,
      sendViaEmail: this.sendViaEmail,
      sendViaPhone: this.sendViaPhone,
    };
    return new Set(
      [...this.enteredByHand].filter((field) => {
        const value = values[field];
        return typeof value === 'string' ? !!value.trim() : value !== null && value !== undefined;
      }),
    );
  }

  /**
   * Fills the recipient with the patient's details. With keepEntered, what the preparer entered by hand stays and
   * the record fills the rest (IOHB-2107): Yes on "Reuse patient Info?" after an upload refilled everything, so an
   * email typed for a patient with none on file was wiped and the send stopped at "Contact Info Incomplete" until
   * it was typed again. Product, 24 Oct: "if they manually put something in, don't overwrite it", "only fill empty
   * boxes", "so null and blanks should be filled". A value the screen only showed was not put in by anyone, so the
   * record fills it as before; for the date of birth, see enteredAndNotBlank.
   */
  populateFromPatient(keepEntered = false): void {
    if (!this.selectedEsocRecord) return;

    const patient = this.selectedEsocRecord?.patientEsocData?.patient || {};
    const eSocPref = this.selectedEsocRecord?.patientEsocData?.eSocEmailPhonePreference || {};
    const kept = keepEntered ? this.enteredAndNotBlank() : new Set<RecipientField>();

    const firstName = patient.firstName || '';
    const lastName = patient.lastName || '';
    if (!kept.has('name')) {
      this.recipientName = `${firstName} ${lastName}`.trim() || this.selectedEsocRecord.patientName || '';
    }

    // #26: If empty, pre-fill from latest earlier addendum on this ioDoc
    const prevPatient = this.lastPatientRecipient(this.activeDoc?._id);

    if (!kept.has('email')) {
      this.recipientEmail = eSocPref.email?.email || patient.contactInfo?.email || prevPatient?.email || '';
    }

    // A number kept keeps the dial code picked with it.
    if (!kept.has('phone')) {
      this.fillPhoneFromPatient(eSocPref.phone, patient, prevPatient);
    }

    if (!kept.has('dob')) {
      this.fillDobFromPatient(patient, prevPatient);
    }

    this.additionalEmail = '';
    this.tickChannels(eSocPref, kept, patient, prevPatient);
    // Only what was kept is still the preparer's own: the rest has just come from the record.
    this.enteredByHand = kept;
  }

  /** The patient's number: the e-sign preference's, then the record's, then the last addendum's. */
  private fillPhoneFromPatient(phonePref: any, patient: any, prevPatient: any): void {
    // The e-sign preference keeps the dial code in its own field and `number` holds national digits
    // only, so passing the number alone left the code as whatever the previously opened patient had.
    this.preferredCountryCode = this.knownDialCode(phonePref?.countryCode);
    if (phonePref?.number) {
      this.setPhoneFromStored(phonePref.number, phonePref.countryCode);
    } else if (patient.contactInfo?.phone) {
      this.setPhoneFromStored(patient.contactInfo?.phone);
    } else if (prevPatient?.phone) {
      this.setPhoneFromStored(prevPatient.phone);
    } else {
      this.recipientPhone = '';
      // Nor the previous patient's dial code, for a number typed in now (IOHB-2107).
      this.recipientCountryCode = this.preferredCountryCode || this.countries[0].dial_code;
    }
  }

  /** The patient's date of birth, else the one the last addendum to the patient went out with. */
  private fillDobFromPatient(patient: any, prevPatient: any): void {
    // Read as a calendar date, not an instant: the ISO form parses as midnight UTC, which is the
    // previous day for any agency user behind UTC.
    const patientDob = parseDateOnly(patient.dob);
    this.recipientDob = patientDob || (prevPatient?.dob ? parseDateOnly(prevPatient.dob) ?? prevPatient.dob : null);
    this.dobFromPatientRecord = !this.recipientDob || !!patient.dob;
  }

  /**
   * Product, 1 Oct: "default ticked if the value exists for email and sms", and whatever is chosen at
   * send is saved to the patient's ioDoc preference. So each box is ticked when there is a contact for
   * it, unless the choice saved there - by ioDoc or by the last addendum sent to the patient - turned
   * it off. Only a contact it can send to counts - an address that is one, a ten-digit number:
   * ticked for any digits at all, a patient with a short number on file had the one-click send
   * refused until someone unticked Phone. A box the preparer ticked or unticked by hand keeps their
   * choice (IOHB-2107). An address or number they typed for a patient with none on file that could be
   * sent to ticks its box whatever was saved: that choice was made with nothing to send to. With one on
   * file the saved choice stands, as before.
   */
  private tickChannels(eSocPref: any, kept: Set<RecipientField>, patient: any, prevPatient: any): void {
    const hasEmail = this.isEmailUsable(this.recipientEmail);
    const hasPhone = this.hasValidPhone;
    const emailOnFile = this.isEmailUsable(eSocPref.email?.email || patient.contactInfo?.email || prevPatient?.email);
    // Ten digits, as hasValidPhone asks; one stored with its dial code has more.
    const phoneOnFile =
      (eSocPref.phone?.number || patient.contactInfo?.phone || prevPatient?.phone || '').replace(/\D/g, '').length >= 10;
    const newEmail = kept.has('email') && !emailOnFile;
    const newPhone = kept.has('phone') && !phoneOnFile;
    if (!kept.has('sendViaEmail')) {
      this.sendViaEmail = hasEmail && (newEmail || eSocPref.email?.preferred !== false);
    }
    if (!kept.has('sendViaPhone')) {
      this.sendViaPhone = hasPhone && (newPhone || eSocPref.phone?.preferred !== false);
    }
    // One of the two is always ticked to begin with: the addendum has to go somewhere. When the saved
    // choice leaves nothing ticked - a preference built from the patient record says false to both, or
    // the channel chosen has no contact now - it is every channel there is a contact for, and the email
    // when there is none, so its field says it is needed. Not over a box set by hand: what the preparer
    // chose stays, and with nothing ticked the screen asks them how to send it.
    if (!this.sendViaEmail && !this.sendViaPhone && !kept.has('sendViaEmail') && !kept.has('sendViaPhone')) {
      this.sendViaEmail = hasEmail || !hasPhone;
      this.sendViaPhone = hasPhone;
    }
  }

  onReusePatientInfoChange(): void {
    if (this.reusePatientInfo) {
      this.populateFromPatient();
    } else {
      // The name and contact details belong to the representative, so they are cleared for entry.
      //
      // IOHB-1909: the date of birth is not one of them. It is the *patient's* - sending to a
      // representative changes who receives it, not whose date of birth is verified - and clearing it
      // blocked sending outright, because hasDob gates the send.
      //
      // What a typo here does NOT do is change the check. Verification compares the recipient's entry
      // against `patient.dob` on the patient record, never against the value stored here, so this
      // field cannot correct a date of birth the record holds. A record that holds none gets the one
      // entered here, saved by the server when the addendum is sent (IOHB-1968).
      //
      // Filled with the representative the last addendum went to, when there is one - "not the
      // patient" is usually the same person again - and cleared for entry otherwise.
      const representative = this.lastRepresentative(this.activeDoc?._id);
      if (representative) {
        this.applyRepresentative(representative);
      } else {
        this.clearRecipientForEntry();
      }
      const fromRecord = this.patientDob;
      if (fromRecord) {
        this.recipientDob = fromRecord;
        this.dobFromPatientRecord = true;
      }
    }
  }

  /** Whether the date of birth on screen came from the patient's record rather than being typed or
   * carried over from an addendum already sent. This, not the value itself, is what the lock reads. */
  private dobFromPatientRecord = false;

  /**
   * A stored dial code, but only when it is really one of ours.
   *
   * `eSocEmailPhonePreference.phone.countryCode` is not always a dial code: where nobody touched the
   * dropdown it is derived by slicing the last ten characters off the patient's contact number, so a
   * record can hold `1`, `0044` or `(555`. Assigned straight into the dropdown, which matches on
   * `dial_code`, those select nothing and the staff member sees an empty country box.
   */
  private knownDialCode(code?: string): string {
    const value = (code || '').trim();
    return this.countries.some((c) => c.dial_code === value) ? value : '';
  }

  /**
   * Split a stored number into its dial code and the national digits the masked box shows.
   *
   * Records hold the two joined, as the rest of the portal writes them, and older ones hold
   * national digits with no code at all. Showing a joined number in a 999-999-9999 box would
   * truncate it, so the code is taken off and put in the dropdown beside it.
   */
  private setPhoneFromStored(stored: string, knownCode?: string): void {
    // Reset first, on every path. The dialog is reused as the next patient opens, so leaving the
    // previous value in place sent one patient's addendum text to another patient's country code -
    // a well-formed number belonging to somebody else, carrying a link to a hospice addendum.
    //
    // Resetting to the default would be the same fault the other way round: a number read back off
    // an addendum carries no code, so a +44 patient's draft would be texted a US number. The
    // patient's own code is used where one is known, and only then the default.
    this.recipientCountryCode =
      this.knownDialCode(knownCode) || this.preferredCountryCode || this.countries[0].dial_code;
    const value = (stored || '').trim();
    if (!value) {
      this.recipientPhone = '';
      return;
    }
    // Longest dial code first: +1 would otherwise match ahead of +1242.
    const match = [...this.countries]
      .map((c) => c.dial_code)
      .sort((a, b) => b.length - a.length)
      .find((code) => value.startsWith(code));
    if (match) {
      this.recipientCountryCode = match;
      this.recipientPhone = value.slice(match.length).replace(/\D/g, '');
      return;
    }
    this.recipientPhone = value;
  }

  /**
   * The number as it is sent: dial code followed by digits only, the same shape the e-sign send
   * uses. Sending the masked national number on its own is what produced "Phone number must have
   * country code" and dropped the text.
   */
  get recipientPhoneToSend(): string {
    const digits = (this.recipientPhone || '').replace(/\D/g, '');
    return digits ? `${this.recipientCountryCode}${digits}` : '';
  }

  /**
   * Hide the Copy Signing Link button when configured at the Agency level.
   */
  get shouldHideCopySigningLink(): boolean {
    return Boolean(
      this.companyHidesCopySigningLink ||
      this.currentAgencyRole?.agency?.hideCopySigningLink ||
      this.currentAgency?.hideCopySigningLink ||
      this.selectedEsocRecord?.patientEsocData?.patient?.agency?.hideCopySigningLink ||
      this.selectedEsocRecord?.patientEsocData?.agency?.hideCopySigningLink ||
      this.selectedEsocRecord?.agency?.hideCopySigningLink
    );
  }

  /** The date of birth held on the patient's record - what the recipient's identity check runs against. */
  get patientDob(): Date | null {
    return parseDateOnly(this.selectedEsocRecord?.patientEsocData?.patient?.dob);
  }

  /**
   * The date of birth is fixed while the field is showing the patient's own, so it cannot be edited
   * into something the recipient will not be able to enter.
   *
   * Not locked when it is showing something else. A document already sent to a representative can
   * carry a different date (populateFromActiveDoc reads `activeDoc.recipient.dob`), and freezing that
   * under a label saying it came from the patient record would state something untrue and leave staff
   * unable to correct it.
   *
   * Not locked either for a patient whose record holds no date of birth, because there would be
   * nothing to lock it to. Verification reads the patient record, not this value, so the one entered
   * here is saved to the record by the server when the addendum is sent (IOHB-1968, IOHB-1911).
   *
   * The lock follows where the value CAME FROM, never what it currently is. Deriving it from the
   * value locked the field the instant someone edited the one case meant to stay editable: the
   * date mask leaves a partial string, PrimeNG's Calendar cannot parse it and sets the model to
   * null, and an empty value read as "showing the patient's date" froze the control mid-edit with
   * the send already blocked by hasDob.
   */
  get dobLocked(): boolean {
    return this.lockedToSignedDoc || (this.dobFromPatientRecord && !!this.patientDob);
  }

  /**
   * Whether the recipient can actually be reached. Presence alone was the test before this, so a
   * document went out to `uyjfhgvyukjhtgvuykjthggu76yhg` and reported two successful sends. The
   * Deliberately looser than the portal's own registration validator in `confirmed.validator.ts`,
   * which additionally constrains the character set and the TLD. Looser is the safe direction: an
   * address the product already accepted elsewhere cannot be rejected here, so no existing patient
   * is stranded. It matches what the backend enforces, so the button and the API agree.
   */
  private static readonly EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** Public so each field can flag itself, rather than one message covering both. */
  isEmailValid(value: string | null | undefined): boolean {
    return this.isEmailUsable(value);
  }

  private isEmailUsable(value: string | null | undefined): boolean {
    const email = (value || '').trim();
    return !!email && email.length <= 254 && HosAddendumComponent.EMAIL_PATTERN.test(email);
  }

  /** Addresses that were typed but cannot be sent to. Drives the message under the field. */
  get invalidEmails(): string[] {
    return [this.recipientEmail, this.showAdditionalEmail ? this.additionalEmail : '']
      .map((e) => (e || '').trim())
      .filter((e) => !!e && !this.isEmailUsable(e));
  }

  get hasEmail(): boolean {
    // Every address typed must be usable, not merely one of them: the second address is sent to as
    // well, so a malformed one is a failed delivery even when the first is fine.
    return (
      (this.isEmailUsable(this.recipientEmail) ||
        (this.showAdditionalEmail && this.isEmailUsable(this.additionalEmail))) &&
      this.invalidEmails.length === 0
    );
  }

  get hasDob(): boolean {
    return !!this.recipientDob;
  }

  /** Ten digits, the rule ioDoc applies to a number it will text. */
  get hasValidPhone(): boolean {
    return /^[0-9]{10}$/.test((this.recipientPhone || '').replace(/\D/g, ''));
  }

  /**
   * Whether the ticked channels can reach the recipient, checked as ioDoc checks its "Send via" boxes:
   * at least one ticked, an address that can be sent to for email, and a ten-digit number for a text.
   * An unusable address stops the send only when Email is ticked: an unticked contact is not sent, as
   * on ioDoc, so a stale address left in the box cannot block a text.
   */
  get hasContact(): boolean {
    if (!this.sendViaEmail && !this.sendViaPhone) {
      return false;
    }
    if (this.sendViaEmail && this.invalidEmails.length > 0) {
      return false;
    }
    if (this.sendViaEmail && !this.hasEmail) {
      return false;
    }
    return !this.sendViaPhone || this.hasValidPhone;
  }

  /** What is sent for the ticked boxes - e-sign's own values. None ticked sends none. */
  get notificationMethod(): 'email' | 'phone' | 'both' | undefined {
    if (this.sendViaEmail && this.sendViaPhone) {
      return 'both';
    }
    if (this.sendViaEmail) {
      return 'email';
    }
    return this.sendViaPhone ? 'phone' : undefined;
  }

  /**
   * The contact a document went to, by the channel it was sent by - as the audit log shows it. A
   * document sent before the channel was recorded went by email, and still shows its email.
   */
  recipientContact(recipient: any): string {
    const email = (recipient?.email || '').trim();
    const phone = (recipient?.phone || '').trim();
    if (recipient?.notificationMethod === 'phone') {
      return phone || 'No phone';
    }
    if (recipient?.notificationMethod === 'both') {
      return [email || 'No email', phone || 'No phone'].join(' | ');
    }
    return email || 'No email';
  }

  /**
   * The label has always carried a red asterisk and nothing ever checked it, so an addendum could go
   * out to somebody the record does not name - the emails and the signing page then both read
   * "Beneficiary / Representative", which is what the server substitutes when no name is supplied.
   *
   * Reuse-patient-info fills this from the patient record and falls back to "Patient", so it is only
   * ever empty when the preparer is entering someone manually and has left it blank.
   */
  get hasRecipientName(): boolean {
    const name = this.recipientName?.trim();
    // The placeholder counts as missing. It is what the server writes when nobody supplied a name,
    // so accepting it here would wave through the one case this check exists for - and the server
    // refuses it anyway, which would show up as an error the screen said was fine.
    return !!name && name !== UNNAMED_RECIPIENT;
  }

  get canAutoSend(): boolean {
    return this.hasRecipientName && this.hasContact && this.hasDob;
  }

  get validationErrorMsg(): string | null {
    // Says what is actually missing. Blank name with blank contact details is the common case -
    // reuse off and nothing typed - and naming one, then the other, sends the preparer back twice.
    if (!this.hasRecipientName && (!this.hasContact || !this.hasDob)) {
      return 'Populate the recipient name and contact info, and then manually send';
    }
    if (!this.hasRecipientName) {
      return 'Enter the name of the person this addendum is going to, and then send.';
    }
    if (!this.sendViaEmail && !this.sendViaPhone) {
      return 'Choose how to send it: tick Send via Email, Send via Phone, or both.';
    }
    // IOHB-1968: with the email and phone typed in, saying "contact info" sent preparers back to the
    // fields they had just filled, when the date of birth was the one left empty.
    if (this.hasContact && !this.hasDob) {
      return "Enter the patient's date of birth, and then send.";
    }
    if (!this.hasContact || !this.hasDob) {
      return 'Populate the contact info of the recipient, and then manually send';
    }
    return null;
  }

  get isSigned(): boolean {
    return this.activeDoc?.status === 'signed' || this.activeDoc?.status === 'completed';
  }

  /**
   * A signed addendum's details are read-only, but a new addendum can still be uploaded after it: the
   * form unlocks once a file for the new one is chosen.
   */
  get lockedToSignedDoc(): boolean {
    return this.isSigned && !this.selectedFile;
  }

  isSignedDoc(doc: any): boolean {
    return doc?.status === 'signed' || doc?.status === 'completed';
  }

  populateFromActiveDoc(): void {
    if (!this.activeDoc?.recipient) return;
    // To tell what this changes on the form (IOHB-2107).
    const wasForPatient = this.reusePatientInfo;
    const before = this.recipientSnapshot();
    const r = this.activeDoc.recipient;
    // Sent to someone other than the patient: shown exactly as sent, empty fields included, so the form
    // says who it went to instead of mixing the patient's details in.
    const exact = r.reusePatientInfo === false;
    // A stored placeholder is left out rather than shown: it reads as a real name in the input, so
    // the preparer would have no reason to replace it.
    this.recipientName =
      (r.name && r.name !== UNNAMED_RECIPIENT ? r.name : '') || (exact ? '' : this.recipientName);
    this.recipientEmail = r.email || (exact ? '' : this.recipientEmail);
    this.additionalEmail = r.additionalEmail || (exact ? '' : this.additionalEmail);
    if (r.phone) {
      this.setPhoneFromStored(r.phone);
    } else if (exact) {
      this.recipientPhone = '';
    }
    if (r.dob) {
      this.recipientDob = parseDateOnly(r.dob) ?? r.dob;
      // An addendum already sent carries its own date of birth, which may not be the patient's.
      this.dobFromPatientRecord = false;
    }
    if (r.reusePatientInfo !== undefined) {
      this.reusePatientInfo = r.reusePatientInfo;
    }
    this.applyChannels(r, this.wasSent(this.activeDoc));

    // #26: If activeDoc is an unsent draft and has empty recipient fields, prefill from latest earlier addendum on same ioDoc
    if (!this.wasSent(this.activeDoc)) {
      if (this.reusePatientInfo) {
        const prevPatient = this.lastPatientRecipient(this.activeDoc._id);
        if (prevPatient) {
          if (!this.recipientEmail && prevPatient.email) {
            this.recipientEmail = prevPatient.email;
          }
          if (!this.recipientPhone && prevPatient.phone) {
            this.setPhoneFromStored(prevPatient.phone);
          }
          if (!this.recipientDob && prevPatient.dob) {
            this.recipientDob = parseDateOnly(prevPatient.dob) ?? prevPatient.dob;
          }
        }
      } else {
        const prevRep = this.lastRepresentative(this.activeDoc._id);
        if (prevRep) {
          if (!this.recipientName && prevRep.name) {
            this.recipientName = prevRep.name !== UNNAMED_RECIPIENT ? prevRep.name : '';
          }
          if (!this.recipientEmail && prevRep.email) {
            this.recipientEmail = prevRep.email;
          }
          if (!this.recipientPhone && prevRep.phone) {
            this.setPhoneFromStored(prevRep.phone);
          }
        }
      }
    }

    // A field the addendum filled no longer holds what the preparer typed; one it left alone still does. Typed
    // while the form was someone else's, it was never the patient's (IOHB-2107).
    if (wasForPatient) {
      this.forgetChangedSince(before);
    } else {
      this.enteredByHand.clear();
    }
  }

  /**
   * Ticks the channels a recipient was reached by. One sent before the choice existed went by email,
   * and by text as well when it had a number, so that is what the boxes show for it. A draft that was
   * never sent holds the patient's details from upload, which are not a choice, so its boxes keep the
   * patient's own preference - ticking Phone because a number is on file would text a patient who
   * declined texts.
   */
  private applyChannels(r: any, sent = true): void {
    if (r.notificationMethod === 'email' || r.notificationMethod === 'phone' || r.notificationMethod === 'both') {
      this.sendViaEmail = r.notificationMethod !== 'phone';
      this.sendViaPhone = r.notificationMethod !== 'email';
    } else if (sent && (r.email || r.phone)) {
      this.sendViaEmail = !!r.email;
      this.sendViaPhone = !!r.phone;
    }
  }

  /**
   * Whether a document went out. Older records were sent without a sentAt, so the status counts too.
   */
  private wasSent(doc: any): boolean {
    return (
      !!doc?.sentAt ||
      (doc?.totalSendCount || 0) > 0 ||
      ['sent', 'pending', 'signed', 'completed', 'expired'].includes(doc?.status)
    );
  }

  /**
   * The recipient details from the latest earlier addendum for this patient (same ioDoc)
   * when going to the patient (reusePatientInfo === true, isPatient !== false).
   */
  private lastPatientRecipient(exceptId?: string): any | null {
    const currentEsocId = this.selectedEsocRecord?.patientEsocData?._id?.toString();
    const when = (d: any) => new Date(d?.sentAt || d?.updatedAt || d?.createdAt || 0).getTime();
    const found = (this.allDocs || [])
      .filter((d: any) => !exceptId || d?._id?.toString() !== exceptId.toString())
      .filter((d: any) => !currentEsocId || !d?.esocId || d.esocId.toString() === currentEsocId)
      .filter((d: any) => this.wasSent(d))
      .filter((d: any) => {
        const r = d?.recipient;
        return !!r && r.isPatient !== false && r.reusePatientInfo !== false && !!(r.email || r.phone || r.dob);
      })
      .sort((a: any, b: any) => when(b) - when(a));
    return found[0]?.recipient || null;
  }

  /**
   * The representative the latest earlier addendum for this patient went to (same ioDoc)
   * when going to a representative (reusePatientInfo === false or isPatient === false).
   */
  private lastRepresentative(exceptId?: string): any | null {
    const currentEsocId = this.selectedEsocRecord?.patientEsocData?._id?.toString();
    const when = (d: any) => new Date(d?.sentAt || d?.updatedAt || d?.createdAt || 0).getTime();
    const found = (this.allDocs || [])
      .filter((d: any) => !exceptId || d?._id?.toString() !== exceptId.toString())
      .filter((d: any) => !currentEsocId || !d?.esocId || d.esocId.toString() === currentEsocId)
      .filter((d: any) => this.wasSent(d))
      .filter((d: any) => {
        const r = d?.recipient;
        return !!r && (r.reusePatientInfo === false || r.isPatient === false) && !!(r.name || r.email || r.phone);
      })
      .sort((a: any, b: any) => when(b) - when(a));
    return found[0]?.recipient || null;
  }

  /** Fills the form with a representative. The date of birth stays the patient's: it is what the link checks. */
  private applyRepresentative(r: any): void {
    this.recipientName = r.name && r.name !== UNNAMED_RECIPIENT ? r.name : '';
    this.recipientEmail = r.email || '';
    this.additionalEmail = '';
    if (r.phone) {
      this.setPhoneFromStored(r.phone);
    } else {
      this.recipientPhone = '';
    }
    this.applyChannels(r);
  }

  /** Empties the recipient's own details for typing. The date of birth stays: it is the patient's. */
  private clearRecipientForEntry(): void {
    this.recipientName = '';
    this.recipientEmail = '';
    this.additionalEmail = '';
    this.recipientPhone = '';
    this.sendViaEmail = true;
    this.sendViaPhone = false;
  }

  get isSubsequentUpload(): boolean {
    // The server decides this and records it: an addendum uploaded after the window a first one would
    // have had counts as subsequent even when there is nothing in the history behind it.
    if (this.activeDoc?.docState === 'subsequent' || this.activeDoc?.fileType === 'subsequent') {
      return true;
    }
    if (this.activeDoc?.docState === 'new') {
      return false;
    }
    return !!(this.historyDocs && this.historyDocs.length > 0);
  }

  get activeClockDuration(): number {
    if (this.activeDoc?.clockDuration) {
      return this.activeDoc.clockDuration;
    }
    if (this.activeDoc?.clockDurationDays) {
      return this.activeDoc.clockDurationDays;
    }
    return this.isSubsequentUpload
      ? this.clockConfig.subsequentClockValue
      : this.clockConfig.initialClockValue;
  }

  get hasEmailDeliveryFailure(): boolean {
    return Boolean(this.activeDoc?.channelFailures?.email?.failed);
  }

  get emailDeliveryError(): string {
    return (
      this.activeDoc?.channelFailures?.email?.error ||
      'The email could not be delivered to the recipient.'
    );
  }

  get hasSmsDeliveryFailure(): boolean {
    return Boolean(this.activeDoc?.channelFailures?.sms?.failed);
  }

  get smsDeliveryError(): string {
    return (
      this.activeDoc?.channelFailures?.sms?.error ||
      'The SMS could not be delivered to the recipient.'
    );
  }

  get isAllChannelsFailed(): boolean {
    return this.activeDoc?.deliveryStatus === 'failed';
  }

  onFileSelected(event: any): void {
    const file = event.target?.files?.[0] || event[0];
    // Cleared once read. The input otherwise keeps the last file's name, and a browser does not report
    // picking that same file again as a change - so after one upload, choosing it again did nothing.
    if (event?.target && 'value' in event.target) {
      event.target.value = '';
    }
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        this.messageHandlerService.generateToastMessage('error', 'Invalid File', 'Please upload a PDF document.');
        return;
      }
      this.selectedFile = file;
      this.uploadDocument();
    }
  }

  removeFile(): void {
    this.selectedFile = null;
  }

  /**
   * `refillRecipient` reads the recipient back off the document: on opening, and after a send, resend or
   * retraction, when the document holds what was sent. Not after an upload or a date change - the
   * preparer may be part way through typing, and the answer to "Reuse patient Info?" fills the form.
   */
  loadHistory(refillRecipient = false, onLoaded?: () => void): void {
    const esocId = this.selectedEsocRecord?.patientEsocData?._id;
    if (!esocId) return;

    this.loading = true;
    this.hosAddendumService
      .getHosAddendumHistory(esocId)
      // finalize also runs when the error interceptor turns a 400/403/500 into a silent completion,
      // which reaches neither callback below and would otherwise leave this spinner on for good.
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          // A slower response for a patient opened earlier must not land on the patient now on screen.
          if (this.selectedEsocRecord?.patientEsocData?._id !== esocId) {
            return;
          }
          if (res?.success && res.data) {
            this.activeDoc = res.data.activeDoc || null;
            this.historyDocs = res.data.historyDocs || [];
            this.allDocs = res.data.allDocs || [];
            if (res.data.clockConfig) {
              this.clockConfig = res.data.clockConfig;
            }
            this.patientSocDate = parseDateOnly(res.data.socDate);
            this.addendumSocDate = parseDateOnly(res.data.hosAddendumSocDate);
            this.socDateInput = this.addendumSocDate;
            // A date already held for this addendum is the one in force; so is the custom option for a
            // patient whose record carries no start of care to fall back to.
            this.socDateSource =
              this.addendumSocDate || !this.patientSocDate ? 'custom' : 'record';
            if (this.activeDoc?.recipient && (refillRecipient || this.isSigned)) {
              this.populateFromActiveDoc();
            } else if (!this.activeDoc) {
              if (this.reusePatientInfo) {
                const prev = this.lastPatientRecipient();
                if (prev) {
                  const before = this.recipientSnapshot();
                  if (!this.recipientEmail && prev.email) this.recipientEmail = prev.email;
                  if (!this.recipientPhone && prev.phone) this.setPhoneFromStored(prev.phone);
                  if (!this.recipientDob && prev.dob) this.recipientDob = parseDateOnly(prev.dob) ?? prev.dob;
                  // A field filled from the last addendum no longer holds what the preparer typed (IOHB-2107).
                  this.forgetChangedSince(before);
                }
              }
            }
            onLoaded?.();
          }
        },
        error: (err) => {
          const msg = err?.error?.message || err?.message || 'Failed to load HOS Addendum history';
          this.messageHandlerService.generateToastMessage('error', 'Error', msg);
        },
      });
  }

  /**
   * Manual refresh triggered from the header button.
   *
   * Reconciles current document status (e.g. signed by recipient, reminder sent, delivery updated),
   * updates active clock, delivery errors, audit history, and emits `onUpdated` to sync the parent e-soc list.
   *
   * The reply is handled by loadHistory, as when the popup opens. Like it, the recipient is refilled only for
   * a sent or signed document - never a draft the preparer may be part way through typing - and a start of
   * care date typed but not yet saved is kept.
   */
  refreshAddendumData(): void {
    const previousStatus = this.activeDoc?.status;
    const typedSocDate =
      this.socDateInput !== this.addendumSocDate ? { input: this.socDateInput, source: this.socDateSource } : null;
    this.loadHistory(this.wasSent(this.activeDoc) || this.isSigned, () => {
      if (typedSocDate) {
        this.socDateInput = typedSocDate.input;
        this.socDateSource = typedSocDate.source;
      }

      // Sync the underlying table in e-soc
      this.onUpdated.emit(this.activeDoc);

      // Re-check company settings
      this.loadCompanySettings(false);

      if (previousStatus !== 'signed' && previousStatus !== 'completed' && this.isSigned) {
        this.messageHandlerService.generateToastMessage('success', 'Signed', 'HOS Addendum has been signed.');
      } else {
        this.messageHandlerService.generateToastMessage('info', 'Refreshed', 'HOS Addendum details refreshed.');
      }
    });
  }

  uploadDocument(): void {
    if (!this.selectedFile) {
      this.messageHandlerService.generateToastMessage(
        'warn',
        'File Required',
        'Please select a HOS Addendum PDF template to upload.',
      );
      return;
    }

    // Every upload is asked which date its clock runs from. This replaces the "restart the signing
    // clock?" prompt: that prompt existed to decide a window the server had inferred, and there is
    // nothing left to infer once the preparer has said which date the document is measured from.
    // Two dialogs with opposite rules on the same action would only contradict each other.
    // Which option the dialog opens on follows the patient's own history. A patient who already has
    // an addendum on record is, by definition, past their first, so the subsequent window is the
    // answer nearly every time. Opening on the initial one meant changing it by hand on every upload
    // after the first - on the most repeated action in the feature - and a preparer who did not
    // notice silently got the longer window. Either option can still be picked; only the starting
    // position moves.
    //
    // A first addendum whose start of care is already further back than the first window opens on
    // the shorter one too. Opening on the start of care would produce an addendum that is past due
    // before it is sent. Product, 28 Sep: "if there is a document prior, default to shorter window,
    // or more than 5 days since SOC date".
    this.uploadDateType = this.hasPriorAddendum || this.socWindowHasPassed ? 'change' : 'soc';
    this.uploadDate = this.defaultUploadDate(this.uploadDateType);
    // Taken fresh each time the dialog opens. It was set once when the component was built, which a
    // date-only picker never noticed - the same calendar day all session. A picker offering a TIME
    // does: on a page left open an hour, the last hour would be unselectable and the default of now
    // would sit above the maximum.
    this.socDateMaxDate = new Date();
    this.showUploadDateDialog = true;
  }

  /**
   * What the date box starts on.
   *
   * The patient's start of care for a first addendum measured from it, and today otherwise. A
   * subsequent addendum is about a change in care, and the preparer is the only one who knows when
   * that happened, so today is a starting point rather than an answer.
   */
  private defaultUploadDate(type: 'soc' | 'change'): Date {
    if (type === 'soc' && !this.hasPriorAddendum && this.patientSocDate) {
      return new Date(this.patientSocDate);
    }
    return new Date();
  }

  onUploadDateTypeChange(): void {
    this.uploadDate = this.defaultUploadDate(this.uploadDateType);
  }

  /**
   * Whether the picker offers a time as well as a date.
   *
   * On an agency measuring its clock in minutes a calendar day is useless - midnight this morning is
   * already hours past a five minute window, and the server ignores a plain date there for that very
   * reason. A moment is the only thing that means anything, so those agencies pick date AND time.
   *
   * Everywhere else the time is deliberately absent: the server reads a picked date as that calendar
   * day, so showing a time would suggest a precision the deadline does not have.
   */
  get uploadDateNeedsTime(): boolean {
    return (this.clockConfig?.clockUnit || 'days') !== 'days';
  }

  /** Whether this patient already has an addendum, which is what decides the default above. */
  get hasPriorAddendum(): boolean {
    return (this.allDocs || []).length > 0;
  }

  /**
   * Whether the first window, counted from the patient's start of care, has already run out.
   *
   * Measured in the frame the backend uses - UTC midnight of the start-of-care date, the same as
   * socDatePreview - and in the agency's own unit and length. At exactly the end of the window it has
   * run out: that is the moment the addendum would be due.
   */
  get socWindowHasPassed(): boolean {
    const soc = this.patientSocDate ? parseDateOnly(this.patientSocDate) : null;
    if (!soc) {
      return false;
    }
    const socUtc = Date.UTC(soc.getFullYear(), soc.getMonth(), soc.getDate());
    const unitMs = this.uploadDateNeedsTime ? 60 * 1000 : 24 * 60 * 60 * 1000;
    const initial = this.clockConfig?.initialClockValue || 5;
    return Date.now() - socUtc >= initial * unitMs;
  }

  /**
   * The deadline the chosen date produces, shown beside the picker.
   *
   * A date the preparer chooses is taken literally - it is day zero, and the window runs from there -
   * so choosing one far enough back produces a document that is already past its deadline. That is
   * the specified behaviour, but it must be visible at the moment of choosing rather than discovered
   * afterwards, which is what this is for.
   */
  get uploadDuePreview(): { due: Date; length: number; unit: string; pastDue: boolean } | null {
    if (!this.uploadDate) {
      return null;
    }
    const length =
      this.uploadDateType === 'soc'
        ? this.clockConfig.initialClockValue || 5
        : this.clockConfig.subsequentClockValue || 3;

    // A minutes agency picks a moment, so the deadline is that moment plus so many minutes and both
    // sides of the comparison are real instants - no calendar arithmetic and no timezone to get wrong.
    if (this.uploadDateNeedsTime) {
      const due = new Date(this.uploadDate.getTime() + length * 60 * 1000);
      return { due, length, unit: 'minutes', pastDue: due.getTime() < Date.now() };
    }

    const anchor = parseDateOnly(this.uploadDate);
    if (!anchor) {
      return null;
    }
    const due = new Date(anchor.getTime());
    due.setDate(due.getDate() + length);
    // Measured the way the server measures it: it keeps the date as UTC midnight and counts from
    // there, so comparing local midnight would move the boundary by the viewer's offset.
    const dueUtc = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
    return { due, length, unit: 'days', pastDue: dueUtc < Date.now() };
  }

  confirmUploadDate(): void {
    if (!this.uploadDate) {
      this.messageHandlerService.generateToastMessage(
        'warn',
        'Date Required',
        'Choose the date this addendum is measured from.',
      );
      return;
    }
    this.showUploadDateDialog = false;
    this.executeUpload();
  }

  /**
   * When the running window closes — the furnishing deadline staff are working to.
   *
   * Read from the value the backend stamps at send where there is one. Addenda sent before that
   * field existed carry none, so the date is derived from the start of the clock and its duration
   * rather than leaving the deadline blank on exactly the older documents most likely to be near it.
   */
  get activeClockDueDate(): Date | null {
    const doc = this.activeDoc;
    if (!doc) {
      return null;
    }
    if (doc.clockExpiresAt) {
      return new Date(doc.clockExpiresAt);
    }
    if (!doc.clockStartedAt) {
      return null;
    }
    const unitMs = doc.clockUnit === 'minutes' ? 60 * 1000 : 24 * 60 * 60 * 1000;
    return new Date(new Date(doc.clockStartedAt).getTime() + this.activeClockDuration * unitMs);
  }

  /** When the recipient was last contacted — the most recent reminder, or the original send. */
  get activeLastSentAt(): Date | null {
    const doc = this.activeDoc;
    const value = doc?.lastAlertSentAt || doc?.sentAt;
    return value ? new Date(value) : null;
  }

  /** What is left of the running window, in its own unit, or null when nothing is running. */
  private get runningClockRemaining(): number | null {
    const doc = this.activeDoc;
    if (!doc?.clockStartedAt) {
      return null;
    }
    const finished =
      doc.status === 'signed' ||
      doc.status === 'completed' ||
      doc.status === 'expired' ||
      doc.status === 'superseded' ||
      doc.isSuperseded;
    if (finished) {
      return null;
    }
    const unitMs = doc.clockUnit === 'minutes' ? 60 * 1000 : 24 * 60 * 60 * 1000;
    const elapsed = (Date.now() - new Date(doc.clockStartedAt).getTime()) / unitMs;
    return (doc.clockDurationDays || this.clockConfig.initialClockValue) - elapsed;
  }

  get clockUnitLabel(): string {
    return this.clockConfig.clockUnit === 'minutes' ? 'minutes' : 'days';
  }

  /**
   * Whether replacing the document is close enough to the deadline to be worth asking about. Nothing is
   * offered while more than a subsequent window is left, so a correction can never buy time back, and
   * nothing is offered once the window has gone either - that upload is a subsequent addendum anyway.
   */
  get canRestartClock(): boolean {
    const remaining = this.runningClockRemaining;
    if (remaining === null || remaining <= 0) {
      return false;
    }
    // "Under three" means nothing across two different units, so there is nothing to compare.
    if ((this.activeDoc?.clockUnit || 'days') !== (this.clockConfig.clockUnit || 'days')) {
      return false;
    }
    return remaining < this.clockConfig.subsequentClockValue;
  }

  get clockRestartMessage(): string {
    const remaining = Math.max(0, this.runningClockRemaining ?? 0);
    const unit = this.clockUnitLabel;
    // Rounding down reads "About 0 days" through the last hours of a window, which is the moment the
    // wording matters most. Below one, say so in words instead of quoting a zero.
    const left =
      remaining < 1
        ? `Less than 1 ${unit.replace(/s$/, '')} is`
        : `About ${Math.floor(remaining * 10) / 10} ${unit} are`;
    return (
      `${left} left on the signing clock. ` +
      `Restart it at ${this.clockConfig.subsequentClockValue} ${unit} for this version, ` +
      `or keep the time that is left?`
    );
  }

  /**
   * Whether the choice of start date is offered at all. It is, for any addendum that is not locked to
   * a document already signed - including a patient whose record carries a start of care, who can be
   * anchored elsewhere deliberately.
   */
  get canEnterSocDate(): boolean {
    return !this.lockedToSignedDoc;
  }

  /** Whether the patient's own record offers a start of care to fall back to. */
  get hasRecordSocDate(): boolean {
    return !!this.patientSocDate;
  }

  /**
   * The date the addendum clock actually runs from. A date entered here outranks the record's own: it
   * was chosen for this addendum knowing what the record holds.
   */
  get effectiveSocDate(): Date | null {
    return this.addendumSocDate || this.patientSocDate;
  }

  /**
   * What the date currently on screen would actually produce, shown before anything is saved so an
   * entry error is caught here rather than after it has moved a CMS deadline.
   *
   * This mirrors the backend's `firstAddendumClock` rather than simply adding the initial window:
   * the full window is given only while at least a subsequent window is still left from the anchor,
   * and otherwise the addendum takes the shorter window *from upload* and is labelled subsequent. A
   * preview that only warned once the deadline had passed would be wrong for every date inside that
   * band - with the default 5 and 3, any date between three and five days ago.
   *
   * `null` where no preview can be honest: on a minutes clock, which is the demo setting and ignores
   * the anchor entirely, and once anything here has been sent, after which saving a date no longer
   * re-anchors anything.
   *
   * `anchored` is false for a date in the future. The backend does not anchor to care that has not
   * begun and gives the full window from upload instead, so the deadline is real but has nothing to
   * do with the date on screen - and saying "5 days from this date" under a mistyped year would be
   * the loudest possible version of the error this preview exists to catch.
   */
  get socDatePreview(): { due: Date; willBeSubsequent: boolean; anchored: boolean } | null {
    // Only a draft nothing was ever sent from is re-anchored by saving a date, and only that draft
    // gets a first addendum's window. Gating on the badge instead would hide the preview in the very
    // case the choice exists for: a draft labelled subsequent *because* its anchor was stale, which
    // entering a real date rescues back to the full window.
    if (this.clockConfig?.clockUnit !== 'days' || this.anyAddendumWasFurnished) {
      return null;
    }
    const from = this.socDateSource === 'custom' ? this.socDateInput : this.patientSocDate;
    const anchor = from ? parseDateOnly(from) : null;
    if (!anchor) {
      return null;
    }
    const initial = this.clockConfig.initialClockValue || 5;
    const subsequent = this.clockConfig.subsequentClockValue || 3;
    const dayMs = 24 * 60 * 60 * 1000;
    // Measured in the frame the backend stores the date in. parseDateOnly builds *local* midnight so
    // the calendar day is right on screen, while the backend keeps 'YYYY-MM-DD' as *UTC* midnight and
    // measures from there. Comparing the two directly moves the boundary below by the viewer's UTC
    // offset - hours in which the page promises a window the upload will not get.
    const anchorUtc = Date.UTC(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    // Care that has not begun opens no window: the backend does not anchor there and gives the full
    // window from upload instead.
    if (anchorUtc > Date.now()) {
      const due = new Date();
      due.setDate(due.getDate() + initial);
      return { due, willBeSubsequent: false, anchored: false };
    }
    const elapsed = (Date.now() - anchorUtc) / dayMs;
    // The same tolerance the backend applies, so a boundary date does not fall either side of it.
    if (initial - elapsed >= subsequent - 1e-6) {
      const due = new Date(anchor.getTime());
      due.setDate(due.getDate() + initial);
      return { due, willBeSubsequent: false, anchored: true };
    }
    const due = new Date();
    due.setDate(due.getDate() + subsequent);
    return { due, willBeSubsequent: true, anchored: true };
  }

  /**
   * Whether anything here was ever sent, which is what decides if saving a date still re-anchors the
   * clock. Mirrors the backend's own `anyWasFurnished`.
   */
  private get anyAddendumWasFurnished(): boolean {
    const furnished = ['sent', 'pending', 'signed', 'completed', 'expired'];
    return (this.allDocs || []).some(
      (d: any) => Boolean(d?.sentAt) || furnished.includes(d?.status),
    );
  }

  saveSocDate(): void {
    const esocId = this.selectedEsocRecord?.patientEsocData?._id;
    if (!esocId) {
      return;
    }

    // Choosing the record's own start of care clears the date held for this addendum, which hands the
    // clock back to it. With nothing on the record to hand back to, the date entered here is the only
    // meaningful answer - and the other option is disabled on screen in that case.
    const source = this.hasRecordSocDate ? this.socDateSource : 'custom';
    // Emptying the field still clears the date for a patient with no start of care on record - there
    // is no other way to say it there. Where the record does hold one, saying so is what the first
    // option is for, so an empty date under the second one is an unfinished entry rather than a clear.
    if (source === 'custom' && !this.socDateInput && this.hasRecordSocDate) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'Not Saved',
        'Enter the date the signing clock should run from.',
      );
      return;
    }

    // Sent as a plain calendar date. Converting to an instant first would move it a day for anyone
    // behind UTC, which is the shift date-only.util exists to avoid.
    const picked = source === 'custom' && this.socDateInput ? parseDateOnly(this.socDateInput) : null;
    const asDateOnly = picked
      ? `${picked.getFullYear()}-${String(picked.getMonth() + 1).padStart(2, '0')}-${String(
          picked.getDate(),
        ).padStart(2, '0')}`
      : null;

    const agencyIdStr = this.resolveAgencyId();

    this.savingSocDate = true;
    this.hosAddendumService
      .setHosAddendumSocDate(esocId, asDateOnly, agencyIdStr)
      .pipe(finalize(() => (this.savingSocDate = false)))
      .subscribe({
        next: (res) => {
          // The save finished for a patient no longer on screen: say nothing and touch nothing here,
          // or this patient's page shows the other one's date.
          if (!this.isStillOnScreen(esocId)) {
            return;
          }
          if (!res?.success) {
            this.messageHandlerService.generateToastMessage(
              'error',
              'Not Saved',
              res?.message || 'Could not save the start of care.',
            );
            return;
          }
          this.addendumSocDate = picked;
          this.messageHandlerService.generateToastMessage(
            'success',
            'Saved',
            asDateOnly
              ? 'The signing clock will run from the date you entered.'
              : "The signing clock will run from the start of care on the patient's record.",
          );
          // The draft's deadline may have moved with it, so read it back rather than guessing.
          this.loadHistory();
        },
        error: (err) => {
          if (!this.isStillOnScreen(esocId)) {
            return;
          }
          this.messageHandlerService.generateToastMessage(
            'error',
            'Not Saved',
            // ErrorInterceptor rethrows anything other than a 400/403/500 as the bare message string.
            (typeof err === 'string' && err) || err?.error?.message || 'Could not save the start of care.',
          );
        },
      });
  }

  executeUpload(resetClock?: boolean): void {
    const esocId = this.selectedEsocRecord?.patientEsocData?._id;
    if (!esocId) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'Error',
        'Patient eSoc record not found.',
      );
      return;
    }

    const agencyIdStr = this.resolveAgencyId();

    const formData = new FormData();
    formData.append('file', this.selectedFile!);
    formData.append('esocId', esocId);
    formData.append('reusePatientInfo', 'true');
    formData.append('autoSend', 'false');
    if (resetClock !== undefined) {
      formData.append('resetClock', String(resetClock));
    }
    // Sent as a plain calendar day. The server stores it as UTC midnight and measures the window from
    // there; sending an ISO timestamp would hand it the viewer's offset and move the deadline.
    if (this.uploadDate) {
      const d = this.uploadDate;
      const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate(),
      ).padStart(2, '0')}`;
      // A plain calendar day everywhere the clock is measured in days: the server reads it as that day
      // and ignores any time, so sending one would only risk the viewer's offset moving the deadline -
      // half past eleven at night in New York is the following morning in UTC.
      //
      // A full moment where the clock is in minutes, because a calendar day cannot express it and the
      // server ignores a plain date on those agencies.
      formData.append('addendumDate', this.uploadDateNeedsTime ? d.toISOString() : day);
      formData.append('addendumDateType', this.uploadDateType);
    }
    this.uploadProgress = 0;
    this.uploading = true;
    this.hosAddendumService
      .uploadTemplate(formData, agencyIdStr, (percent) => (this.uploadProgress = percent))
      .pipe(finalize(() => (this.uploading = false)))
      .subscribe({
      next: (res) => {
        // The upload finished for a patient no longer on screen: refresh the list, but never put that
        // document into this dialog or open the "Reuse patient Info?" prompt, which can send it.
        if (!this.isStillOnScreen(esocId)) {
          if (res?.success) this.onUpdated.emit(res.data);
          return;
        }
        if (res?.success) {
          this.selectedFile = null;
          this.activeDoc = res.data;
          this.loadHistory();
          this.onUpdated.emit(res.data);

          // Prompt: "Reuse patient Info?"
          // Said straight after the upload, while another file can still be chosen, and before the question
          // below, whose Yes can send the document at once.
          this.warnIfSignatureLineNotFound(res.data, () => this.promptReusePatientInfoAfterUpload(res.data));
        } else {
          this.messageHandlerService.generateToastMessage(
            'error',
            'Upload Failed',
            res?.message || 'Failed to upload',
          );
        }
      },
      error: (err) => {
        const msg =
          err?.error?.message ||
          err?.message ||
          'Failed to upload HOS Addendum';
        this.messageHandlerService.generateToastMessage('error', 'Error', msg);
      },
    });
  }

  promptReusePatientInfoAfterUpload(uploadedDoc?: any): void {
    const targetDoc = uploadedDoc || this.activeDoc;
    this.confirmationService.confirm({
      key: 'hosAddendumConfirm',
      header: 'Recipient Contact Info',
      message: 'Reuse patient Info?',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        // What was entered for the patient before the upload stays (IOHB-2107). A form showing someone else -
        // Reuse patient info unticked - is refilled with the patient, as Yes says.
        const keepEntered = this.reusePatientInfo;
        this.reusePatientInfo = true;
        this.populateFromPatient(keepEntered);
        if (targetDoc) {
          this.activeDoc = targetDoc;
        }
        if (this.hasContact && this.hasDob) {
          this.sendActiveDoc();
        } else {
          this.messageHandlerService.generateToastMessage(
            'warn',
            'Contact Info Incomplete',
            'Patient contact info (email/DOB) is incomplete. Please populate recipient details and click Send.',
          );
        }
      },
      reject: (type?: ConfirmEventType) => {
        if (targetDoc) {
          this.activeDoc = targetDoc;
        }
        // The X, or Escape, only closes the question: the document is uploaded and nothing on screen
        // changes, so the preparer is never pushed down either path. "No" is the answer that means
        // someone other than the patient.
        if (type === ConfirmEventType.CANCEL) {
          return;
        }
        // The date of birth stays either way: it is the patient's, and it verifies the signer when
        // they open the link, whoever is signing.
        this.reusePatientInfo = false;
        const representative = this.lastRepresentative(targetDoc?._id);
        if (representative) {
          this.applyRepresentative(representative);
          this.messageHandlerService.generateToastMessage(
            'info',
            'Representative',
            'Document uploaded. The representative from the last addendum is filled in - check the details and click Send.',
          );
          return;
        }
        this.clearRecipientForEntry();
        this.messageHandlerService.generateToastMessage(
          'info',
          'Manual Entry',
          'Document uploaded. Please enter recipient details manually and click Send.',
        );
      },
    });
  }

  uploadAndProcess(sendImmediately: boolean = false): void {
    this.uploadDocument();
  }

  private formatDob(dob: any): string {
    if (!dob) return '';
    if (dob instanceof Date) {
      const y = dob.getFullYear();
      const m = String(dob.getMonth() + 1).padStart(2, '0');
      const d = String(dob.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    if (typeof dob === 'string') {
      return dob.trim();
    }
    return '';
  }

  /**
   * What reading the document said. 'match' means this patient's MRN is on the page; 'mismatch' that
   * the page was read and carries a different one; 'unreadable' that nothing could be read, usually
   * a scan. Absent on anything uploaded before this existed, which is treated as nothing to say.
   */
  /** The patient this addendum belongs to, for messages that need to name them. */
  get patientName(): string {
    const first = this.selectedEsocRecord?.patientEsocData?.firstName || "";
    const last = this.selectedEsocRecord?.patientEsocData?.lastName || "";
    return `${first} ${last}`.trim() || this.selectedEsocRecord?.patientName || "";
  }

  get mrnScanOutcome(): string | null {
    return this.activeDoc?.mrnScan?.outcome || null;
  }

  /**
   * Right after an upload whose signature lines could not be found, says so while another file can still be
   * chosen, then goes on to `next`. "Upload a different file" opens the file picker instead, and nothing else
   * happens. "Keep this file" is the answer for this document, so Send does not ask again. The X or Escape
   * closes it without an answer: the upload goes on as usual and Send still asks.
   */
  private warnIfSignatureLineNotFound(doc: any, next: () => void): void {
    if (doc?.signaturePlacement !== "fixed" || !doc?._id) {
      next();
      return;
    }
    const docId = String(doc._id);
    this.confirmationService.confirm({
      // Not hosAddendumConfirm: `next` asks its question there, and a dialog drops a question asked from inside
      // its own button as it closes.
      key: "hosPlacementWarning",
      header: "Signature line not found",
      message:
        "We could not find the signature line on this document, so the signature and date will be placed at the standard positions, which may not line up with this form. A PDF exported from HCHB, rather than a scan or a printed copy, works best.",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Upload a different file",
      rejectLabel: "Keep this file",
      accept: () => this.chooseAnotherFile(),
      reject: (type?: ConfirmEventType) => {
        if (type !== ConfirmEventType.CANCEL) {
          this.placementAcknowledgedDocId = docId;
        }
        next();
      },
    });
  }

  /**
   * "Upload a different file". The file input is on the Upload tab, which is not on screen if the preparer moved to
   * History while the file went up - so that tab is shown first, or the button would do nothing.
   */
  private chooseAnotherFile(): void {
    if (!this.fileInputRef) {
      this.activeTab = "upload";
      this.cdr.detectChanges();
    }
    this.fileInputRef?.nativeElement.click();
  }

  /**
   * The upload could not find the signature lines (#23, Hassaan 5 Oct), so the signature and date will go at
   * the standard positions. The preparer is asked before sending, once per document - right after the upload,
   * or at Send when that was not answered.
   */
  get placementNeedsConfirming(): boolean {
    const doc = this.activeDoc;
    return doc?.signaturePlacement === "fixed" && !!doc?._id && this.placementAcknowledgedDocId !== String(doc._id);
  }

  get mrnNeedsConfirming(): boolean {
    return this.mrnNeedsConfirmingFor(this.activeDoc);
  }

  private mrnNeedsConfirmingFor(doc: any): boolean {
    const outcome = doc?.mrnScan?.outcome;
    if (outcome !== "mismatch" && outcome !== "unreadable") {
      return false;
    }
    return !doc?._id || this.mrnAcknowledgedDocId !== String(doc._id);
  }

  private askAboutMrn(doc: any, send: () => void): void {
    this.mrnWarningDocId = doc?._id ? String(doc._id) : null;
    this.sendAwaitingMrnAnswer = send;
    this.mrnWarningVisible = true;
  }

  /** Yes: send it. Remembered for this document, so the same document does not ask twice. */
  confirmMrnWarningAndSend(): void {
    this.mrnAcknowledgedDocId = this.mrnWarningDocId;
    this.mrnWarningVisible = false;
    const send = this.sendAwaitingMrnAnswer || (() => this.sendActiveDoc(true));
    this.sendAwaitingMrnAnswer = null;
    send();
  }

  /** No: nothing is sent and the addendum stays as it is, for the preparer to correct. */
  dismissMrnWarning(): void {
    this.mrnWarningVisible = false;
    this.sendAwaitingMrnAnswer = null;
  }

  sendActiveDoc(mrnAnswered = false): void {
    if (this.placementNeedsConfirming) {
      const docId = String(this.activeDoc._id);
      this.confirmationService.confirm({
        // Not hosAddendumConfirm: the Yes of "Reuse patient Info?" there sends, and a dialog drops a question asked
        // from inside its own button as it closes - so nothing was sent and nothing was said.
        key: "hosPlacementWarning",
        header: "Signature line not found",
        message:
          "We could not find the signature line on this document, so the signature and date will be placed at the standard positions, which may not line up with this form. Send anyway?",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Send anyway",
        rejectLabel: "Cancel",
        accept: () => {
          this.placementAcknowledgedDocId = docId;
          this.sendActiveDoc(mrnAnswered);
        },
      });
      return;
    }

    // Asked here rather than at upload, because this is the moment the document actually goes to
    // someone. Staff may have good reason to attach a document we could not read, so this asks
    // rather than refuses - but it asks before it leaves, not after.
    if (!mrnAnswered && this.mrnNeedsConfirming) {
      this.askAboutMrn(this.activeDoc, () => this.sendActiveDoc(true));
      return;
    }

    if (!this.hasRecipientName || !this.hasContact || !this.hasDob) {
      this.messageHandlerService.generateToastMessage(
        'error',
        !this.hasRecipientName
          ? 'Recipient Name Required'
          : this.hasContact && !this.hasDob
            ? 'Date of Birth Required'
            : 'Contact Info Required',
        this.validationErrorMsg || 'Populate the contact info of the recipient, and then manually send',
      );
      return;
    }

    const esocId = this.selectedEsocRecord?.patientEsocData?._id;
    if (!esocId) return;

    const formattedDob = this.formatDob(this.recipientDob);

    const payload = {
      esocId,
      hosAddendumId: this.activeDoc?._id,
      recipient: {
        name: this.recipientName,
        // An unticked contact is not checked, so an unusable one left in its box is not sent either -
        // the server refuses any bad address, and a stale one would block a text. A usable one is kept,
        // so it is there the next time the form opens.
        email: this.sendViaEmail || this.isEmailUsable(this.recipientEmail) ? this.recipientEmail : '',
        additionalEmail: this.showAdditionalEmail ? this.additionalEmail : '',
        phone: this.sendViaPhone || this.hasValidPhone ? this.recipientPhoneToSend : '',
        dob: formattedDob,
        isPatient: this.reusePatientInfo,
        reusePatientInfo: this.reusePatientInfo,
        notificationMethod: this.notificationMethod,
      },
      customMessage: this.customMessage,
    };

    const agencyIdStr = this.resolveAgencyId();

    const isResending = !!(
      this.activeDoc &&
      (this.activeDoc.status === 'sent' || this.activeDoc.status === 'pending' || this.activeDoc.sentAt)
    );

    this.sending = true;
    this.hosAddendumService
      .sendHosAddendum(payload, agencyIdStr)
      .pipe(finalize(() => (this.sending = false)))
      .subscribe({
      next: (res) => {
        if (!this.isStillOnScreen(esocId)) {
          if (res?.success) this.onUpdated.emit(res.data);
          return;
        }
        if (res?.success) {
          const title = isResending ? 'Resent' : 'Sent';
          const msg = isResending
            ? 'HOS Addendum resent to recipient successfully'
            : 'HOS Addendum sent to recipient successfully';
          this.messageHandlerService.generateToastMessage('success', title, msg);
          this.loadHistory(true);
          this.onUpdated.emit(res.data);
        }
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Failed to send HOS Addendum';
        this.messageHandlerService.generateToastMessage('error', 'Error', msg);
      },
    });
  }

  resendDoc(doc: any, mrnAnswered = false): void {
    if (!doc?._id) return;
    const esocId = this.selectedEsocRecord?.patientEsocData?._id;
    if (!esocId) return;

    // A resend is a send too. It went out once already, but the document still carries someone
    // else's number, or none that could be read, so it asks the same question first.
    if (!mrnAnswered && this.mrnNeedsConfirmingFor(doc)) {
      this.askAboutMrn(doc, () => this.resendDoc(doc, true));
      return;
    }

    const recipient = doc.recipient || {};
    if (!recipient.email && !recipient.phone) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'Contact Info Required',
        'Recipient has no valid email or phone on file.',
      );
      return;
    }

    // Resend is its own send path and had no name check at all, while substituting the placeholder
    // below - so it manufactured exactly the value the server rejects. Guarded here so the preparer
    // is told on the screen rather than by a failed request.
    const resendName = (recipient.name || this.recipientName || '').trim();
    if (!resendName || resendName === UNNAMED_RECIPIENT) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'Recipient Name Required',
        'Enter the name of the person this addendum is going to, and then send.',
      );
      return;
    }

    const formattedDob = this.formatDob(recipient.dob || this.recipientDob);

    const payload = {
      esocId,
      hosAddendumId: doc._id,
      recipient: {
        name: resendName,
        email: recipient.email || this.recipientEmail,
        additionalEmail: this.showAdditionalEmail ? recipient.additionalEmail || this.additionalEmail : '',
        phone: recipient.phone || this.recipientPhoneToSend,
        dob: formattedDob,
        isPatient: recipient.isPatient !== undefined ? recipient.isPatient : true,
        reusePatientInfo: recipient.reusePatientInfo !== undefined ? recipient.reusePatientInfo : true,
        // As saved, like the rest of this recipient. One saved without a choice is sent without one,
        // and the server keeps what it went by - the boxes on screen belong to the open document.
        ...(recipient.notificationMethod ? { notificationMethod: recipient.notificationMethod } : {}),
      },
      customMessage: this.customMessage,
    };

    const agencyIdStr = this.resolveAgencyId();

    this.sending = true;
    this.hosAddendumService
      .sendHosAddendum(payload, agencyIdStr)
      .pipe(finalize(() => (this.sending = false)))
      .subscribe({
      next: (res) => {
        if (!this.isStillOnScreen(esocId)) {
          if (res?.success) this.onUpdated.emit(res.data);
          return;
        }
        if (res?.success) {
          this.messageHandlerService.generateToastMessage(
            'success',
            'Resent',
            'HOS Addendum resent to recipient successfully',
          );
          this.loadHistory(true);
          this.onUpdated.emit(res.data);
        }
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Failed to resend HOS Addendum';
        this.messageHandlerService.generateToastMessage('error', 'Error', msg);
      },
    });
  }

  sendFromHistory(doc: any): void {
    if (!doc) return;
    this.activeDoc = doc;
    if (doc.recipient) {
      this.populateFromActiveDoc();
    }
    if (this.hasRecipientName && this.hasContact && this.hasDob) {
      this.sendActiveDoc();
    } else {
      this.activeTab = 'upload';
      this.messageHandlerService.generateToastMessage(
        'info',
        this.hasRecipientName ? 'Contact Info Required' : 'Recipient Name Required',
        this.hasRecipientName
          ? 'Please review or enter the recipient contact details and click Send.'
          : 'Please enter the name of the person this addendum is going to, and click Send.',
      );
    }
  }

  downloadPdf(fileLocation: string, fileName: string): void {
    if (!fileLocation) return;
    this.downloadingFile = true;
    this.esocService
      .getEPHfileObjectUrl({ fileName: fileLocation })
      // Same as the uploads and sends: the error interceptor turns 400/403/500 into a silent completion
      // that reaches neither callback, which left the full-screen "Preparing Document" overlay up for good.
      .pipe(finalize(() => (this.downloadingFile = false)))
      .subscribe({
      next: (res) => {
        if (res?.success && res.url) {
          const a = document.createElement('a');
          a.href = res.url;
          a.download = fileName || 'HOS_Addendum.pdf';
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          this.messageHandlerService.generateToastMessage(
            'error',
            'Download Failed',
            res?.message || 'Error generating download URL',
          );
        }
      },
      error: (err) => {
        this.messageHandlerService.generateToastMessage(
          'error',
          'Download Failed',
          err?.message || 'Error downloading file',
        );
      },
    });
  }

  downloadAuditLog(doc: any): void {
    const auditId = doc?.auditId || doc?.sendGroupId || doc?.esignFileId || doc?._id;
    if (!auditId) {
      this.messageHandlerService.generateToastMessage('warn', 'Unavailable', 'Audit reference not found for this document.');
      return;
    }
    const fileName = `audit-${auditId}.pdf`;
    this.downloadingFile = true;
    this.esocService
      .getEPHfileObjectUrl({ fileName })
      .pipe(finalize(() => (this.downloadingFile = false)))
      .subscribe({
      next: (res) => {
        if (res?.success && res.url) {
          const a = document.createElement('a');
          a.href = res.url;
          a.download = fileName;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          this.messageHandlerService.generateToastMessage(
            'error',
            'Download Failed',
            res?.message || 'Audit log not ready or not found.',
          );
        }
      },
      error: (err) => {
        this.messageHandlerService.generateToastMessage(
          'error',
          'Download Failed',
          err?.message || 'Error downloading audit log.',
        );
      },
    });
  }

  /** The file exactly as it was uploaded, before any stamping or signing. */
  downloadOriginal(doc: any): void {
    this.downloadPdf(
      doc?.originalFileLocation || doc?.fileLocation,
      `ORIGINAL_${doc?.fileOriginalName || 'HOS_Addendum.pdf'}`,
    );
  }

  downloadMergedDocument(doc: any): void {
    const fileLoc = doc?.mergedFileLocation;
    if (!fileLoc) {
      if (!this.isTerminalDoc(doc)) {
        this.messageHandlerService.generateToastMessage(
          'info',
          'In-Flight Document',
          'The merged document with final compliance certificate is generated once the addendum reaches a terminal state (Signed, Expired, or Superseded). You can download the current Addendum and Audit Log individually above.',
        );
      } else {
        this.messageHandlerService.generateToastMessage(
          'warn',
          'Preparing',
          'Merged document is being prepared. Please refresh or try again in a moment.',
        );
      }
      return;
    }
    const fileName = fileLoc;
    const downloadDisplayName =
      doc?.mergedFileName ||
      `MERGED_${doc?.fileOriginalName || 'HOS_Addendum.pdf'}`;

    this.downloadingFile = true;
    this.esocService
      .getEPHfileObjectUrl({ fileName })
      .pipe(finalize(() => (this.downloadingFile = false)))
      .subscribe({
      next: (res) => {
        if (res?.success && res.url) {
          const a = document.createElement('a');
          a.href = res.url;
          a.download = downloadDisplayName;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          this.messageHandlerService.generateToastMessage(
            'error',
            'Download Failed',
            res?.message || 'Merged document not ready or not found.',
          );
        }
      },
      error: (err) => {
        this.messageHandlerService.generateToastMessage(
          'error',
          'Download Failed',
          err?.message || 'Error downloading merged document.',
        );
      },
    });
  }

  isTerminalDoc(doc: any): boolean {
    if (!doc) return false;
    const s = doc.status;
    return (
      s === 'signed' ||
      s === 'completed' ||
      s === 'expired' ||
      s === 'superseded' ||
      doc.isSuperseded === true
    );
  }

  // The addendum no longer carries its link's code (security review M3), so the link is asked for. The
  // server refuses when the agency hides copying, and records each copy in the audit trail.
  copyLink(doc: any): void {
    if (!doc?._id) return;
    // No reply means the HTTP wrapper ended a refused call and has already shown why, so it is not shown
    // twice; a refusal with a reply carries the server's own reason to the message.
    const alreadyShown = new Error('Already shown');
    const link = firstValueFrom(this.hosAddendumService.getHosAddendumSigningLink(String(doc._id)), {
      defaultValue: null,
    }).then((res: any) => {
      if (res === null) throw alreadyShown;
      if (!res?.data?.link) throw Object.assign(new Error('No signing link'), { error: { message: res?.message } });
      return String(res.data.link);
    });
    void Promise.all([link, this.copyWhenReady(link)])
      .then(([value, copied]) => (copied ? undefined : navigator.clipboard.writeText(value)))
      .then(
        () => this.messageHandlerService.generateToastMessage('info', 'Copied', 'E-Sign link copied to clipboard'),
        (err: any) =>
          err === alreadyShown
            ? undefined
            : this.messageHandlerService.generateToastMessage(
                'error',
                'Error',
                err?.error?.message || 'Could not copy the signing link.',
              ),
      );
  }

  // Safari and Firefox only let a page write the clipboard during the click, so the copy starts now and
  // waits for the text. False when the browser cannot do that; the caller then writes the text itself.
  private copyWhenReady(text: Promise<string>): Promise<boolean> {
    const blob = text.then((value) => new Blob([value], { type: 'text/plain' }));
    void blob.catch(() => undefined); // a refused link is reported once, by the caller
    try {
      return navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })]).then(
        () => true,
        () => false,
      );
    } catch {
      return Promise.resolve(false);
    }
  }
}
